import {
  MAX_UPLOAD_FILE_BYTES,
  MAX_UPLOAD_TOTAL_BYTES,
  type FolderUploadSummary,
} from "../../lib/folder";

interface ManifestReviewProps {
  includedCount: number;
  omitted: string[];
  mode?: "build" | "publish";
  summary: FolderUploadSummary;
}

export function ManifestReview({ includedCount, omitted, mode = "publish", summary }: ManifestReviewProps) {
  const includedLabel = mode === "build"
    ? `${includedCount} ${includedCount === 1 ? "file" : "files"} will enter the private build`
    : `${includedCount} ${includedCount === 1 ? "file" : "files"} will go online`;
  const omittedLabel = `${omitted.length} generated or private ${omitted.length === 1 ? "file stays" : "files stay"} on this computer`;
  return (
    <section className="manifest-review" aria-label="What will be uploaded">
      <p className="manifest-heading">What happens when you publish</p>
      <dl className="manifest-sizes">
        <div><dt>Total folder size</dt><dd>{formatBytes(summary.totalBytes)}</dd></div>
        <div><dt>Ignored size</dt><dd>{formatBytes(summary.ignoredBytes)}</dd></div>
        <div><dt>Actual upload size</dt><dd>{formatBytes(summary.uploadBytes)}</dd></div>
      </dl>
      <div className="manifest-result manifest-result-good">
        <span aria-hidden="true">✓</span>
        <div>
          <strong>{includedLabel}</strong>
          <p>{mode === "build" ? "Only the finished static output will go online." : "These are the finished website files Doorway will send."}</p>
        </div>
      </div>
      {omitted.length > 0 && (
        <div className="manifest-result manifest-result-private">
          <span aria-hidden="true">⌂</span>
          <div>
            <strong>{omittedLabel}</strong>
            <p className="manifest-paths">
              {omitted.slice(0, 5).join(", ")}
              {omitted.length > 5 ? ` and ${omitted.length - 5} more` : ""}
            </p>
          </div>
        </div>
      )}
      {summary.largeFolders.map((folder) => (
        <div className="manifest-result manifest-result-warning" key={folder.path}>
          <span aria-hidden="true">!</span>
          <div>
            <strong>Large folder found</strong>
            <p><code>{folder.path}</code> contributes {formatBytes(folder.bytes)} to the upload.</p>
          </div>
        </div>
      ))}
      {summary.oversizedFiles.map((file) => (
        <div className="manifest-result manifest-result-blocked" key={file.path} role="alert">
          <span aria-hidden="true">×</span>
          <div>
            <strong>File is too large</strong>
            <p><code>{file.path}</code> is larger than the {formatBytes(MAX_UPLOAD_FILE_BYTES)} file limit. Its size is {formatBytes(file.bytes)}.</p>
          </div>
        </div>
      ))}
      {summary.uploadBytes > MAX_UPLOAD_TOTAL_BYTES && (
        <div className="manifest-result manifest-result-blocked" role="alert">
          <span aria-hidden="true">×</span>
          <div>
            <strong>Upload is too large</strong>
            <p>The remaining upload is larger than {formatBytes(MAX_UPLOAD_TOTAL_BYTES)}. Choose a smaller project folder or remove unneeded files.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${rounded(bytes / 1024)} KiB`;
  return `${rounded(bytes / 1024 / 1024)} MiB`;
}

function rounded(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/u, "");
}
